import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import {
    Calendar, Send, Loader2, AlertCircle, ChevronLeft, ChevronRight,
    Trash2, ShieldAlert, Search, CheckSquare, Eye, CheckCircle2,
    Clock, Info, Filter, X, RotateCcw, ArrowRightLeft,
    CalendarDays, CalendarRange, Infinity as InfinityIcon, Sparkles
} from 'lucide-react';
import CalendarView from '../components/CalendarView';

const getLocalTodayString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateInput = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const MONTH_NAMES_TR = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

export default function EmployeeAssignmentPage() {
    const { isAuthenticated, permissions } = useAuth();

    const [employees, setEmployees] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('ALL');
    const [regimeFilter, setRegimeFilter] = useState('ALL');
    const [scheduleStatusFilter, setScheduleStatusFilter] = useState('ALL'); // 'ALL' | 'UNASSIGNED' | 'DIFF_OR_TEMP' | 'ASSIGNED'
    const [showOnlySelected, setShowOnlySelected] = useState(false);

    // Sayfalama (Sol Panel)
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Çoklu personel seçimi ve Önizleme
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [previewEmployeeId, setPreviewEmployeeId] = useState('');

    const [workPeriods, setWorkPeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState('');
    const [startDate, setStartDate] = useState(getLocalTodayString);
    const [useEmployeeHireDate, setUseEmployeeHireDate] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [isTemporaryAssignment, setIsTemporaryAssignment] = useState(false);

    // Takvim Görünüm Modu ('month' | 'year')
    const [calendarMode, setCalendarMode] = useState('month');
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [viewDate, setViewDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [loadingCalendar, setLoadingCalendar] = useState(false);
    const [error, setError] = useState(null);
    const [successBanner, setSuccessBanner] = useState(null);
    const [operationResults, setOperationResults] = useState(null);

    // Silme Onay Modalı (Önceki vardiyayı uzat vs. vardiyasız bırak)
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        assignment: null,
        previousAssignment: null,
        submitting: false
    });

    const canView = useMemo(() => permissions.includes('assignments:view'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('assignments:create'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('assignments:delete'), [permissions]);

    useEffect(() => {
        if (!isTemporaryAssignment) setEndDate('');
    }, [isTemporaryAssignment]);

    // Geçici atama için hızlı bitiş tarihi kısayolları
    const applyQuickDuration = (type) => {
        const base = startDate ? new Date(`${startDate}T00:00:00`) : new Date();
        const target = new Date(base);

        if (type === '1W') {
            target.setDate(target.getDate() + 6);
        } else if (type === '2W') {
            target.setDate(target.getDate() + 13);
        } else if (type === '1M') {
            target.setMonth(target.getMonth() + 1);
            target.setDate(target.getDate() - 1);
        } else if (type === 'EOM') {
            target.setMonth(target.getMonth() + 1, 0);
        } else if (type === 'EOY') {
            target.setMonth(11, 31);
        }

        setIsTemporaryAssignment(true);
        setEndDate(formatDateInput(target));
    };

    const refreshEmployeesList = useCallback(async () => {
        try {
            const empData = await apiClient('Employee/dropdown', { method: 'GET' });
            setEmployees(empData || []);
        } catch (err) {
            console.error("Personel listesi yenilenemedi:", err);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !canView) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const [empData, periodData] = await Promise.all([
                    apiClient('Employee/dropdown', { method: 'GET' }),
                    apiClient('scheduling/periods', { method: 'GET' })
                ]);

                setEmployees(empData || []);
                setWorkPeriods(periodData || []);
            } catch (err) {
                setError(err.message || "Gerekli veriler yüklenemedi.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isAuthenticated, canView]);

    const departmentOptions = useMemo(() => {
        const set = new Set();
        employees.forEach(e => {
            const dName = e.department?.name || e.departmentName;
            if (dName) set.add(dName);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [employees]);

    const regimeOptions = useMemo(() => {
        const set = new Set();
        employees.forEach(e => {
            if (e.workingRegimeName) set.add(e.workingRegimeName);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [employees]);

    const selectedSet = useMemo(() => new Set(selectedEmployeeIds), [selectedEmployeeIds]);

    // Filtrelenmiş Personel Listesi
    const filteredEmployees = useMemo(() => {
        const lowerQuery = searchQuery.trim().toLocaleLowerCase('tr-TR');
        return employees.filter(emp => {
            if (showOnlySelected && !selectedSet.has(emp.id)) return false;

            const deptName = emp.department?.name || emp.departmentName || '';
            if (departmentFilter !== 'ALL') {
                if (departmentFilter === 'NONE' && deptName) return false;
                if (departmentFilter !== 'NONE' && deptName !== departmentFilter) return false;
            }

            if (regimeFilter !== 'ALL') {
                if (regimeFilter === 'NONE' && emp.workingRegimeName) return false;
                if (regimeFilter !== 'NONE' && emp.workingRegimeName !== regimeFilter) return false;
            }

            if (scheduleStatusFilter === 'UNASSIGNED' && emp.currentWorkPeriodId) return false;
            if (scheduleStatusFilter === 'ASSIGNED' && !emp.currentWorkPeriodId) return false;
            if (scheduleStatusFilter === 'DIFF_OR_TEMP' && !(emp.isCurrentScheduleDifferentFromRegime || emp.isCurrentScheduleTemporary)) return false;

            if (!lowerQuery) return true;
            const nameMatch = emp.fullName?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const idMatch = emp.identityNumber?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const sicilMatch = emp.sicilNo?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const deptMatch = deptName.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const locMatch = emp.locationName?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const regimeMatch = emp.workingRegimeName?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            const periodMatch = emp.currentWorkPeriodName?.toLocaleLowerCase('tr-TR').includes(lowerQuery);
            return nameMatch || idMatch || sicilMatch || deptMatch || locMatch || regimeMatch || periodMatch;
        });
    }, [employees, searchQuery, departmentFilter, regimeFilter, scheduleStatusFilter, showOnlySelected, selectedSet]);

    // Filtre değiştiğinde 1. sayfaya dön
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, departmentFilter, regimeFilter, scheduleStatusFilter, showOnlySelected, pageSize]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredEmployees.length / pageSize)), [filteredEmployees.length, pageSize]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedEmployees = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredEmployees.slice(start, start + pageSize);
    }, [filteredEmployees, currentPage, pageSize]);

    const isAllFilteredSelected = useMemo(() => {
        return filteredEmployees.length > 0 && filteredEmployees.every(e => selectedSet.has(e.id));
    }, [filteredEmployees, selectedSet]);

    const isAllPageSelected = useMemo(() => {
        return paginatedEmployees.length > 0 && paginatedEmployees.every(e => selectedSet.has(e.id));
    }, [paginatedEmployees, selectedSet]);

    const fetchAssignmentHistory = useCallback(async (employeeId) => {
        if (!employeeId || !canView) return;

        setLoadingCalendar(true);
        setError(null);
        try {
            const data = await apiClient(`scheduling/assignments-by-employee?employeeId=${employeeId}`);
            setAssignmentHistory(data || []);
        } catch (err) {
            setError('Personelin atama geçmişi yüklenemedi.');
            setAssignmentHistory([]);
        } finally {
            setLoadingCalendar(false);
        }
    }, [canView]);

    useEffect(() => {
        if (previewEmployeeId) {
            fetchAssignmentHistory(previewEmployeeId);
        } else {
            setAssignmentHistory([]);
        }
    }, [previewEmployeeId, fetchAssignmentHistory]);

    // Filtrelenen tüm personelleri seç / kaldır
    const handleSelectAllFiltered = () => {
        if (filteredEmployees.length === 0) return;
        const filteredIds = filteredEmployees.map(e => e.id);
        if (isAllFilteredSelected) {
            const filteredIdSet = new Set(filteredIds);
            setSelectedEmployeeIds(prev => prev.filter(id => !filteredIdSet.has(id)));
        } else {
            setSelectedEmployeeIds(prev => Array.from(new Set([...prev, ...filteredIds])));
        }
    };

    // Sadece aktif sayfadaki personelleri seç / kaldır
    const handleSelectCurrentPage = () => {
        if (paginatedEmployees.length === 0) return;
        const pageIds = paginatedEmployees.map(e => e.id);
        if (isAllPageSelected) {
            const pageIdSet = new Set(pageIds);
            setSelectedEmployeeIds(prev => prev.filter(id => !pageIdSet.has(id)));
        } else {
            setSelectedEmployeeIds(prev => Array.from(new Set([...prev, ...pageIds])));
        }
    };

    const handleClearSelection = () => {
        setSelectedEmployeeIds([]);
        setShowOnlySelected(false);
    };

    const toggleEmployeeSelect = (id) => {
        setSelectedEmployeeIds(prev => {
            const exists = prev.includes(id);
            const next = exists ? prev.filter(eId => eId !== id) : [...prev, id];
            if (!exists && !previewEmployeeId) {
                setPreviewEmployeeId(id);
            }
            return next;
        });
    };

    const handleSaveAssignment = async () => {
        if (!canCreate) {
            setError("Yeni atama yapma yetkiniz bulunmamaktadır.");
            return;
        }
        if (selectedEmployeeIds.length === 0) {
            setError("Lütfen işlem yapılacak en az bir personel seçin.");
            return;
        }
        if (!selectedPeriodId || (!useEmployeeHireDate && !startDate)) {
            setError("Lütfen bir periyot ve başlangıç tarihi seçin.");
            return;
        }
        if (isTemporaryAssignment && !endDate) {
            setError("Geçici atama seçeneği aktifken lütfen bir bitiş tarihi belirleyin.");
            return;
        }
        if (!useEmployeeHireDate && isTemporaryAssignment && startDate && endDate && new Date(startDate) > new Date(endDate)) {
            setError("Başlangıç tarihi, bitiş tarihinden sonra olamaz.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessBanner(null);
        setOperationResults(null);

        const payload = {
            employeeIds: selectedEmployeeIds,
            workPeriodId: parseInt(selectedPeriodId, 10),
            startDate: startDate || getLocalTodayString(),
            useEmployeeHireDate: useEmployeeHireDate,
            endDate: (isTemporaryAssignment && endDate) ? endDate : null
        };

        try {
            const res = await apiClient('scheduling/assignments/bulk', { method: 'POST', body: payload });
            setOperationResults(res);
            await refreshEmployeesList();

            if (previewEmployeeId && selectedSet.has(previewEmployeeId)) {
                fetchAssignmentHistory(previewEmployeeId);
            }
        } catch (err) {
            setError(err.message || "Toplu atama kaydedilirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Silme butonuna tıklandığında önceki vardiyayı tespit edip onay modalını açar
    const openDeleteAssignmentModal = (assignment) => {
        if (!canDelete) {
            setError("Atama silme yetkiniz bulunmamaktadır.");
            return;
        }

        const targetStart = new Date(assignment.startDate).getTime();
        const prevAssignment = assignmentHistory
            .filter(a => a.id !== assignment.id && new Date(a.startDate).getTime() < targetStart)
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0] || null;

        setDeleteModal({
            open: true,
            assignment,
            previousAssignment: prevAssignment,
            submitting: false
        });
    };

    const confirmDeleteAssignment = async (fillGapWithPrevious) => {
        const assignment = deleteModal.assignment;
        if (!assignment) return;

        setDeleteModal(prev => ({ ...prev, submitting: true }));
        setLoadingCalendar(true);
        setError(null);
        setSuccessBanner(null);

        try {
            const res = await apiClient(
                `scheduling/assignments/${assignment.id}?fillGapWithPrevious=${fillGapWithPrevious ? 'true' : 'false'}`,
                { method: 'DELETE' }
            );
            setDeleteModal({ open: false, assignment: null, previousAssignment: null, submitting: false });
            if (res && res.message) {
                setSuccessBanner(res.message);
            }
            await refreshEmployeesList();
            if (previewEmployeeId) {
                fetchAssignmentHistory(previewEmployeeId);
            }
        } catch (err) {
            setError(err.message || "Atama silinirken bir hata oluştu.");
            setDeleteModal(prev => ({ ...prev, submitting: false }));
        } finally {
            setLoadingCalendar(false);
        }
    };

    const handlePrevPeriod = () => {
        if (calendarMode === 'year') {
            setViewDate(new Date(viewDate.getFullYear() - 1, viewDate.getMonth(), 1));
        } else {
            setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
        }
    };

    const handleNextPeriod = () => {
        if (calendarMode === 'year') {
            setViewDate(new Date(viewDate.getFullYear() + 1, viewDate.getMonth(), 1));
        } else {
            setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
        }
    };

    const handleTodayJump = () => {
        setViewDate(new Date());
    };

    const handleSelectMonthFromYearView = (monthIndex) => {
        setViewDate(new Date(viewDate.getFullYear(), monthIndex, 1));
        setCalendarMode('month');
    };

    const selectedPeriodObj = useMemo(() => {
        if (!selectedPeriodId) return null;
        return workPeriods.find(p => p.id === parseInt(selectedPeriodId, 10)) || null;
    }, [workPeriods, selectedPeriodId]);

    const selectedPeriodDays = useMemo(() => {
        if (!selectedPeriodObj) return [];
        return [
            { label: 'Pzt', group: selectedPeriodObj.mondayShiftGroup },
            { label: 'Sal', group: selectedPeriodObj.tuesdayShiftGroup },
            { label: 'Çar', group: selectedPeriodObj.wednesdayShiftGroup },
            { label: 'Per', group: selectedPeriodObj.thursdayShiftGroup },
            { label: 'Cum', group: selectedPeriodObj.fridayShiftGroup },
            { label: 'Cmt', group: selectedPeriodObj.saturdayShiftGroup },
            { label: 'Paz', group: selectedPeriodObj.sundayShiftGroup }
        ];
    }, [selectedPeriodObj]);

    // Tek bir personel seçiliyse işe giriş tarihini göster
    const singleSelectedEmployee = useMemo(() => {
        if (selectedEmployeeIds.length !== 1) return null;
        return employees.find(e => e.id === selectedEmployeeIds[0]) || null;
    }, [selectedEmployeeIds, employees]);

    // Geçici atamada toplam gün sayısını hesapla
    const tempDayCount = useMemo(() => {
        if (!isTemporaryAssignment || !endDate) return null;
        const effectiveStartStr = useEmployeeHireDate
            ? (singleSelectedEmployee?.hireDate ? singleSelectedEmployee.hireDate.split('T')[0] : null)
            : startDate;
        if (!effectiveStartStr) return null;
        const s = new Date(`${effectiveStartStr}T00:00:00`);
        const e = new Date(`${endDate}T00:00:00`);
        const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
        return diff > 0 ? diff : null;
    }, [isTemporaryAssignment, startDate, endDate, useEmployeeHireDate, singleSelectedEmployee]);

    const yearOptions = useMemo(() => {
        const curY = new Date().getFullYear();
        const arr = [];
        for (let y = curY - 3; y <= curY + 3; y++) arr.push(y);
        return arr;
    }, []);

    if (!loading && !canView) {
        return (
            <div className="flex h-full items-center justify-center p-6">
                <div className="p-8 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-2xl shadow-xl text-center backdrop-blur-sm max-w-md">
                    <div className="bg-red-100 dark:bg-red-900/40 p-4 rounded-full inline-block mb-4">
                        <ShieldAlert className="h-10 w-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-red-800 dark:text-red-300">Yetkisiz Erişim</h3>
                    <p className="mt-2 text-red-600 dark:text-red-400">Bu modülü görüntüleme yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    const previewEmployee = employees.find(e => e.id === previewEmployeeId);

    return (
        <div className="p-4 md:p-6 lg:p-8 space-y-6 bg-gray-50/50 dark:bg-gray-900/30 min-h-[calc(100vh-80px)]">

            {error && (
                <div className="flex-shrink-0 flex items-center justify-between p-4 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded-xl border border-red-200 dark:border-red-800/40 shadow-sm animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center">
                        <AlertCircle size={20} className="mr-3 flex-shrink-0" />
                        <span className="font-medium text-sm">{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 p-1">
                        <X size={16} />
                    </button>
                </div>
            )}

            {successBanner && (
                <div className="flex-shrink-0 flex items-center justify-between p-4 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800/40 shadow-sm animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center">
                        <CheckCircle2 size={20} className="mr-3 flex-shrink-0" />
                        <span className="font-medium text-sm">{successBanner}</span>
                    </div>
                    <button onClick={() => setSuccessBanner(null)} className="text-emerald-500 hover:text-emerald-700 p-1">
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                {/* SOL PANEL - SAYFA BAZLI PERSONEL LİSTESİ */}
                <div className="lg:col-span-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 flex flex-col overflow-hidden transition-all duration-200 sticky top-6" style={{ height: "calc(100vh - 110px)" }}>

                    {/* Arama ve Filtreler */}
                    <div className="p-3.5 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/80 shrink-0 space-y-2">
                        <div className="relative group">
                            <input
                                type="text"
                                placeholder="Ad, Soyad, Sicil, Bölüm, Rejim veya Vardiya..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 dark:focus:ring-blue-600/20 outline-none transition-all duration-200 shadow-sm placeholder:text-gray-400"
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={15} />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-1.5">
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="w-full px-2 py-1.5 text-[11px] font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 outline-none truncate"
                                title="Bölüm Filtresi"
                            >
                                <option value="ALL">Tüm Bölümler</option>
                                {departmentOptions.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                                <option value="NONE">Bölümsüz</option>
                            </select>

                            <select
                                value={regimeFilter}
                                onChange={(e) => setRegimeFilter(e.target.value)}
                                className="w-full px-2 py-1.5 text-[11px] font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 outline-none truncate"
                                title="Çalışma Rejimi Filtresi"
                            >
                                <option value="ALL">Tüm Rejimler</option>
                                {regimeOptions.map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                                <option value="NONE">Rejimsiz</option>
                            </select>

                            <select
                                value={scheduleStatusFilter}
                                onChange={(e) => setScheduleStatusFilter(e.target.value)}
                                className="w-full px-2 py-1.5 text-[11px] font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 outline-none truncate"
                                title="Vardiya Durumu Filtresi"
                            >
                                <option value="ALL">Tüm Vardiyalar</option>
                                <option value="UNASSIGNED">Vardiyasızlar</option>
                                <option value="DIFF_OR_TEMP">Farklı / Geçici</option>
                                <option value="ASSIGNED">Atanmış Olanlar</option>
                            </select>
                        </div>
                    </div>

                    {/* Seçim Kontrol Çubuğu (Tümünü Seç / Bu Sayfayı Seç / Seçililer) */}
                    {filteredEmployees.length > 0 && (
                        <div className="px-3.5 py-2.5 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800 shrink-0 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleSelectAllFiltered}
                                    className="flex items-center text-xs font-bold text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group"
                                    title="Filtrelenen tüm sayfalardaki personelleri seçer veya seçimi kaldırır"
                                >
                                    <div className={`flex items-center justify-center w-4 h-4 mr-1.5 rounded border transition-colors ${isAllFilteredSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300 dark:bg-gray-900 dark:border-gray-600 group-hover:border-blue-400'}`}>
                                        {isAllFilteredSelected && <CheckSquare className="text-white w-3.5 h-3.5" />}
                                    </div>
                                    Tümünü Seç ({filteredEmployees.length})
                                </button>

                                {totalPages > 1 && (
                                    <button
                                        type="button"
                                        onClick={handleSelectCurrentPage}
                                        className={`text-[11px] px-2 py-0.5 rounded-md font-semibold border transition-colors ${
                                            isAllPageSelected
                                                ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/40 dark:border-indigo-700 dark:text-indigo-300'
                                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400'
                                        }`}
                                        title="Sadece bu sayfadaki personelleri seçer veya kaldırır"
                                    >
                                        {isAllPageSelected ? 'Sayfayı Bırak' : `Bu Sayfa (${paginatedEmployees.length})`}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-1.5">
                                {selectedEmployeeIds.length > 0 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setShowOnlySelected(prev => !prev)}
                                            className={`text-[11px] px-2 py-0.5 rounded-md font-bold border transition-colors ${
                                                showOnlySelected
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                            }`}
                                            title="Sadece seçili personelleri listele"
                                        >
                                            {selectedEmployeeIds.length} Seçili
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleClearSelection}
                                            className="text-[11px] px-1.5 py-0.5 rounded text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20 font-semibold"
                                            title="Tüm seçimi temizle"
                                        >
                                            Temizle
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sayfa Bazlı Personel Listesi */}
                    <div className="overflow-y-auto flex-1 p-2.5 stylish-scrollbar relative">
                        {loading && employees.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                                <Loader2 className="animate-spin mb-3" size={28} />
                                <span className="text-sm font-medium">Personeller Yükleniyor...</span>
                            </div>
                        ) : filteredEmployees.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-center px-4">
                                <Filter size={32} className="mb-3 opacity-50" />
                                <span className="text-sm font-medium">Kayıt Bulunamadı</span>
                                {showOnlySelected && (
                                    <button
                                        type="button"
                                        onClick={() => setShowOnlySelected(false)}
                                        className="mt-2 text-xs text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                                    >
                                        Tüm listeye dön
                                    </button>
                                )}
                            </div>
                        ) : (
                            <ul className="space-y-1.5 pb-1">
                                {paginatedEmployees.map(emp => {
                                    const isSelected = selectedSet.has(emp.id);
                                    const isPreview = previewEmployeeId === emp.id;

                                    return (
                                        <li
                                            key={emp.id}
                                            onClick={() => toggleEmployeeSelect(emp.id)}
                                            className={`
                                                group flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-all duration-150 border
                                                ${isSelected
                                                    ? 'bg-blue-50/80 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800/40'
                                                    : 'bg-white border-transparent hover:bg-gray-50 dark:bg-transparent dark:hover:bg-gray-800/50'
                                                }
                                                ${isPreview ? 'ring-2 ring-blue-500/40' : ''}
                                            `}
                                        >
                                            <div className="flex items-center flex-1 min-w-0">
                                                <div className={`flex items-center justify-center w-5 h-5 mr-2.5 shrink-0 rounded border transition-colors ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600'}`}>
                                                    {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3.5 h-3.5"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                                                </div>
                                                <div className="flex-1 min-w-0 pr-2">
                                                    <div className="flex items-center justify-between gap-1.5">
                                                        <span className={`text-sm font-semibold truncate block ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-700 dark:text-gray-200'}`}>
                                                            {emp.fullName}
                                                        </span>
                                                        {emp.workingRegimeName && (
                                                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-semibold border border-indigo-200/60 dark:border-indigo-800/60">
                                                                {emp.workingRegimeName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap overflow-hidden">
                                                        <span className="truncate max-w-[130px] shrink-0" title={emp.department?.name || emp.departmentName || 'Bölüm Yok'}>
                                                            {emp.department?.name || emp.departmentName || 'Bölüm Yok'}
                                                        </span>
                                                        {emp.sicilNo && (
                                                            <>
                                                                <span className="text-gray-300 dark:text-gray-600 shrink-0">•</span>
                                                                <span className="truncate font-mono tracking-tight shrink-0">{emp.sicilNo}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="mt-1 flex items-center gap-1 flex-wrap">
                                                        {emp.currentWorkPeriodName ? (
                                                            <>
                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold truncate max-w-[180px] ${
                                                                    emp.isCurrentScheduleTemporary || emp.isCurrentScheduleDifferentFromRegime
                                                                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                                                                        : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                                                }`} title={`Şu Anki Vardiya: ${emp.currentWorkPeriodName}`}>
                                                                    Şu An: {emp.currentWorkPeriodName}
                                                                </span>
                                                                {emp.isCurrentScheduleTemporary && (
                                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 font-medium">
                                                                        Geçici
                                                                    </span>
                                                                )}
                                                                {!emp.isCurrentScheduleTemporary && emp.isCurrentScheduleDifferentFromRegime && (
                                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 font-medium" title={`Rejim Varsayılanı: ${emp.regimeDefaultWorkPeriodName || '-'}`}>
                                                                        Rejimden Farklı
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 font-medium">
                                                                Vardiya Atanmamış
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPreviewEmployeeId(isPreview ? '' : emp.id);
                                                }}
                                                className={`
                                                    ml-1.5 p-1.5 rounded-lg shrink-0 transition-all duration-200
                                                    ${isPreview
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                        : 'text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                                                    }
                                                `}
                                                title={isPreview ? "Önizlemeyi Kapat" : "Takvimi Önizle"}
                                            >
                                                <Eye size={17} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Alt Sayfalama (Pagination) Çubuğu */}
                    {filteredEmployees.length > 0 && (
                        <div className="px-3.5 py-2.5 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-800/90 shrink-0 flex items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                <span>Sayfa başı:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 font-semibold outline-none"
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    className="p-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    title="Önceki Sayfa"
                                >
                                    <ChevronLeft size={15} />
                                </button>
                                <span className="px-2 font-bold text-gray-700 dark:text-gray-200 select-none">
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    type="button"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    className="p-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    title="Sonraki Sayfa"
                                >
                                    <ChevronRight size={15} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* SAĞ PANEL */}
                <div className="lg:col-span-8 flex flex-col gap-6">

                    {/* ATAMA FORMU & İŞLEM ÖNİZLEMESİ */}
                    {canCreate && (
                        <div className="bg-white dark:bg-gray-800 p-6 lg:p-7 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 flex-shrink-0 relative overflow-hidden">
                            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 blur-3xl opacity-50 pointer-events-none"></div>

                            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 relative z-10">

                                {/* Sol Taraf: Atama Parametreleri (7 Sütun) */}
                                <div className="xl:col-span-7 space-y-5">
                                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                        <h2 className="text-base font-bold text-gray-800 dark:text-white flex items-center">
                                            <div className="bg-indigo-100 dark:bg-indigo-900/50 p-1.5 rounded-lg mr-2.5">
                                                <Clock className="text-indigo-600 dark:text-indigo-400" size={18} />
                                            </div>
                                            Atama Parametreleri
                                        </h2>
                                    </div>

                                    {/* 1. Çalışma Periyodu Seçimi */}
                                    <div>
                                        <label htmlFor="period-select" className="block text-xs font-bold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400">
                                            1. Çalışma Periyodu Seçin
                                        </label>
                                        <select
                                            id="period-select"
                                            value={selectedPeriodId}
                                            onChange={(e) => setSelectedPeriodId(e.target.value)}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-sm rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 dark:focus:ring-blue-600/20 block p-3 outline-none transition-all font-semibold"
                                            disabled={loading}
                                        >
                                            <option value="">Lütfen çalışma periyodu seçiniz...</option>
                                            {workPeriods.map(period => (<option key={period.id} value={period.id}>{period.name}</option>))}
                                        </select>

                                        {/* Seçilen Periyodun 7 Günlük Mini Önizlemesi */}
                                        {selectedPeriodObj && (
                                            <div className="mt-2.5 p-3 rounded-xl bg-slate-50/90 dark:bg-gray-900/60 border border-slate-200/80 dark:border-gray-700/70">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                                                        Haftalık Vardiya Planı
                                                    </span>
                                                    {selectedPeriodObj.hasDynamicDayOff && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold">
                                                            Dinamik Tatil ({selectedPeriodObj.dynamicDayOffCount || 1} Gün)
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-7 gap-1.5 text-center">
                                                    {selectedPeriodDays.map(d => (
                                                        <div
                                                            key={d.label}
                                                            className={`p-1.5 rounded-lg border text-[10px] transition-all ${
                                                                d.group
                                                                    ? 'bg-blue-50/90 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/70 text-blue-900 dark:text-blue-200 shadow-2xs'
                                                                    : 'bg-gray-100/80 dark:bg-gray-800/80 border-gray-200/70 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                                                            }`}
                                                            title={d.group ? d.group.name : 'Hafta Tatili'}
                                                        >
                                                            <div className="font-extrabold opacity-75">{d.label}</div>
                                                            <div className="truncate font-semibold mt-0.5">
                                                                {d.group ? d.group.name : 'Tatil'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. Atama Süresi Türü (Sürekli vs Geçici Segment Seçici) */}
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400">
                                            2. Atama Türü ve Tarih Aralığı
                                        </label>

                                        <div className="grid grid-cols-2 gap-2.5 p-1.5 bg-slate-100 dark:bg-gray-900 rounded-2xl border border-slate-200/80 dark:border-gray-700/80">
                                            <button
                                                type="button"
                                                onClick={() => setIsTemporaryAssignment(false)}
                                                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                                                    !isTemporaryAssignment
                                                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-slate-200 dark:ring-gray-700'
                                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                                }`}
                                            >
                                                <InfinityIcon size={17} className={!isTemporaryAssignment ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />
                                                <span>Sürekli (Kalıcı)</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setIsTemporaryAssignment(true)}
                                                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                                                    isTemporaryAssignment
                                                        ? 'bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 shadow-sm ring-1 ring-amber-200 dark:ring-amber-700/60'
                                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                                }`}
                                            >
                                                <CalendarRange size={17} className={isTemporaryAssignment ? 'text-amber-500' : 'text-gray-400'} />
                                                <span>Bitiş Tarihli (Geçici)</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Tarih Giriş Alanları */}
                                    <div className="space-y-3">
                                        {/* Başlangıç Tarihi Kaynağı Seçici (Belirli Tarih vs İşe Giriş Tarihi) */}
                                        <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/80 dark:border-gray-700/70 space-y-2.5">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                                    Başlangıç Tarihi Seçimi
                                                </span>
                                                <div className="inline-flex rounded-lg p-0.5 bg-gray-200/80 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                                                    <button
                                                        type="button"
                                                        onClick={() => setUseEmployeeHireDate(false)}
                                                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                                                            !useEmployeeHireDate
                                                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-300 shadow-2xs'
                                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                                                        }`}
                                                    >
                                                        Belirli Tarih Seç
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setUseEmployeeHireDate(true)}
                                                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                                                            useEmployeeHireDate
                                                                ? 'bg-indigo-600 text-white shadow-2xs'
                                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                                                        }`}
                                                    >
                                                        İşe Başladığı Tarihten İtibaren
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`grid grid-cols-1 ${isTemporaryAssignment ? 'sm:grid-cols-2' : ''} gap-3`}>
                                                <div>
                                                    {useEmployeeHireDate ? (
                                                        <div className="p-2.5 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/70 flex items-center justify-between min-h-[42px]">
                                                            <div className="text-xs">
                                                                <span className="font-bold text-indigo-900 dark:text-indigo-200 block">
                                                                    Personelin İşe Giriş Tarihi
                                                                </span>
                                                                <span className="text-[11px] text-indigo-700 dark:text-indigo-300 font-medium">
                                                                    {singleSelectedEmployee?.hireDate
                                                                        ? `${singleSelectedEmployee.fullName}: ${new Date(singleSelectedEmployee.hireDate).toLocaleDateString('tr-TR')}`
                                                                        : selectedEmployeeIds.length > 1
                                                                            ? `Seçili ${selectedEmployeeIds.length} personelin kendi işe giriş tarihi baz alınır`
                                                                            : 'Seçilen personelin işe başladığı günden itibaren başlar'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-1.5">
                                                            <input
                                                                type="date"
                                                                id="start-date"
                                                                value={startDate}
                                                                onChange={(e) => setStartDate(e.target.value)}
                                                                className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-sm rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none block p-2.5 transition-all font-semibold"
                                                                disabled={loading}
                                                            />
                                                            <div className="flex items-center gap-1.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setStartDate(getLocalTodayString())}
                                                                    className="text-[10px] px-2 py-0.5 rounded bg-gray-200/70 hover:bg-gray-300/70 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold transition-colors"
                                                                >
                                                                    Bugün
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const now = new Date();
                                                                        setStartDate(formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)));
                                                                    }}
                                                                    className="text-[10px] px-2 py-0.5 rounded bg-gray-200/70 hover:bg-gray-300/70 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold transition-colors"
                                                                >
                                                                    Bu Ayın Başı
                                                                </button>
                                                                {singleSelectedEmployee?.hireDate && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setStartDate(singleSelectedEmployee.hireDate.split('T')[0])}
                                                                        className="text-[10px] px-2 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold transition-colors"
                                                                    >
                                                                        İşe Giriş ({new Date(singleSelectedEmployee.hireDate).toLocaleDateString('tr-TR')})
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {isTemporaryAssignment && (
                                                    <div className="animate-in fade-in slide-in-from-top-1 duration-150">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <label htmlFor="end-date" className="block text-xs font-bold text-amber-700 dark:text-amber-300">
                                                                Bitiş Tarihi
                                                            </label>
                                                            {tempDayCount && (
                                                                <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
                                                                    Toplam {tempDayCount} Gün
                                                                </span>
                                                            )}
                                                        </div>
                                                        <input
                                                            type="date"
                                                            id="end-date"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                            min={useEmployeeHireDate ? undefined : startDate}
                                                            className="w-full bg-amber-50/40 dark:bg-gray-900 border border-amber-300 dark:border-amber-700 text-gray-800 dark:text-gray-100 text-sm rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 outline-none block p-2.5 transition-all font-semibold"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Geçici Atama Hızlı Süre Kısayolları */}
                                        {isTemporaryAssignment && (
                                            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                                                <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mr-1">
                                                    Hızlı Süre:
                                                </span>
                                                {[
                                                    { key: '1W', label: '1 Hafta' },
                                                    { key: '2W', label: '2 Hafta' },
                                                    { key: '1M', label: '1 Ay' },
                                                    { key: 'EOM', label: 'Ay Sonuna Kadar' },
                                                    { key: 'EOY', label: 'Yıl Sonuna Kadar' }
                                                ].map(preset => (
                                                    <button
                                                        key={preset.key}
                                                        type="button"
                                                        onClick={() => applyQuickDuration(preset.key)}
                                                        className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200/80 dark:bg-amber-950/40 dark:hover:bg-amber-900/50 dark:text-amber-300 dark:border-amber-800/60 transition-colors"
                                                    >
                                                        {preset.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Bilgilendirme Kutusu */}
                                        <div className={`flex items-start text-xs p-3.5 rounded-xl border leading-relaxed font-medium ${
                                            isTemporaryAssignment
                                                ? 'bg-amber-50/60 dark:bg-amber-950/20 border-amber-200/70 dark:border-amber-800/50 text-amber-900 dark:text-amber-200'
                                                : 'bg-blue-50/60 dark:bg-blue-950/20 border-blue-200/70 dark:border-blue-800/50 text-blue-900 dark:text-blue-200'
                                        }`}>
                                            <Info size={16} className={`mr-2.5 mt-0.5 shrink-0 ${isTemporaryAssignment ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
                                            <div>
                                                {isTemporaryAssignment ? (
                                                    <p>
                                                        <strong className="font-bold">Geçici Süreli Atama: </strong>
                                                        Seçilen tarih aralığına yeni vardiyayı yerleştirir. Personelin o tarihlerde sürekli bir vardiyası varsa onu iptal etmez; geçici süre bittiğinde ertesi gün otomatik olarak eski vardiyasına geri döner.
                                                    </p>
                                                ) : (
                                                    <p>
                                                        <strong className="font-bold">Kalıcı (Sürekli) Atama: </strong>
                                                        Başlangıç tarihinden itibaren geçerli açık uçlu vardiya atar. Önceki açık vardiyayı başlangıç tarihinden bir gün önce kapatır.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Sağ Taraf: İşlem Önizlemesi & Onay Kutusu (5 Sütun) */}
                                <div className="xl:col-span-5 flex flex-col">
                                    <div className="flex-1 bg-gradient-to-b from-slate-50 to-white dark:from-gray-900/60 dark:to-gray-800/90 rounded-2xl border border-slate-200/90 dark:border-gray-700 p-5 shadow-inner flex flex-col justify-between">
                                        <div className="space-y-4">
                                            <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-gray-400 flex items-center justify-between border-b border-slate-200/70 dark:border-gray-700/60 pb-2.5">
                                                <span className="flex items-center">
                                                    <Sparkles size={14} className="mr-1.5 text-blue-500" /> İşlem Özeti
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    isTemporaryAssignment
                                                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                                                }`}>
                                                    {isTemporaryAssignment ? 'Geçici Atama' : 'Sürekli Atama'}
                                                </span>
                                            </h3>

                                            <div className="space-y-3 text-sm">
                                                <div className="p-3 rounded-xl bg-white dark:bg-gray-900/70 border border-slate-200/60 dark:border-gray-700/60 flex items-center justify-between">
                                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Seçili Personel</span>
                                                    <span className={`font-extrabold ${selectedEmployeeIds.length > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                                                        {selectedEmployeeIds.length > 0 ? `${selectedEmployeeIds.length} Kişi` : 'Seçilmedi'}
                                                    </span>
                                                </div>

                                                <div className="p-3 rounded-xl bg-white dark:bg-gray-900/70 border border-slate-200/60 dark:border-gray-700/60 flex items-center justify-between gap-2">
                                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">Atanacak Periyot</span>
                                                    <span className="font-bold text-gray-800 dark:text-gray-100 text-right truncate">
                                                        {selectedPeriodObj?.name || 'Seçilmedi'}
                                                    </span>
                                                </div>

                                                <div className="p-3 rounded-xl bg-white dark:bg-gray-900/70 border border-slate-200/60 dark:border-gray-700/60 flex flex-col gap-1">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Geçerlilik Aralığı</span>
                                                        {tempDayCount && (
                                                            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                                                                {tempDayCount} Gün
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="font-bold text-gray-800 dark:text-gray-100 text-right text-xs sm:text-sm">
                                                        {useEmployeeHireDate
                                                            ? (singleSelectedEmployee?.hireDate
                                                                ? `${new Date(singleSelectedEmployee.hireDate).toLocaleDateString('tr-TR')} (İşe Giriş)`
                                                                : 'İşe Giriş Tarihi')
                                                            : (startDate ? new Date(`${startDate}T00:00:00`).toLocaleDateString('tr-TR') : '-')}
                                                        <span className="mx-1.5 text-gray-400">➔</span>
                                                        {isTemporaryAssignment
                                                            ? (endDate ? new Date(`${endDate}T00:00:00`).toLocaleDateString('tr-TR') : 'Bitiş Tarihi Seçiniz')
                                                            : 'Sürekli (Açık Uçlu)'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-5">
                                            <button
                                                type="button"
                                                onClick={handleSaveAssignment}
                                                className={`w-full py-3.5 px-4 rounded-xl font-bold flex items-center justify-center transition-all duration-200 shadow-sm
                                                    ${selectedEmployeeIds.length > 0 && selectedPeriodId && (useEmployeeHireDate || startDate) && (!isTemporaryAssignment || endDate)
                                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20 hover:shadow-blue-500/40'
                                                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800 cursor-not-allowed border border-gray-200 dark:border-gray-700'
                                                    }`}
                                                disabled={loading || selectedEmployeeIds.length === 0 || !selectedPeriodId || (!useEmployeeHireDate && !startDate) || (isTemporaryAssignment && !endDate)}
                                            >
                                                {loading ? (
                                                    <Loader2 className="animate-spin mr-2" size={20} />
                                                ) : (
                                                    <Send className="mr-2" size={18} />
                                                )}
                                                İşlemi Onayla ve Ata
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* İşlem Sonuç Banner'ı */}
                            {operationResults && (
                                <div className={`mt-6 p-4 rounded-xl border flex items-start justify-between animate-in fade-in slide-in-from-bottom-2 ${operationResults.success > 0 ? (operationResults.failed > 0 ? 'bg-amber-50/80 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/50' : 'bg-emerald-50/80 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700/50') : 'bg-red-50/80 border-red-200 dark:bg-red-900/20 dark:border-red-700/50'}`}>
                                    <div className="flex items-start">
                                        <div className="shrink-0 mt-0.5">
                                            <CheckCircle2 size={24} className={`${operationResults.failed > 0 ? (operationResults.success === 0 ? 'text-red-500' : 'text-amber-500') : 'text-emerald-500'}`} />
                                        </div>
                                        <div className="ml-3">
                                            <h4 className={`text-sm font-bold ${operationResults.failed > 0 ? (operationResults.success === 0 ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300') : 'text-emerald-800 dark:text-emerald-300'}`}>
                                                Toplu İşlem Özeti
                                            </h4>
                                            <p className="text-sm mt-1 text-gray-600 dark:text-gray-300 font-medium">
                                                Seçili <strong>{operationResults.total}</strong> personelden
                                                <span className="text-emerald-600 dark:text-emerald-400 font-bold mx-1">{operationResults.success}</span> başarılı,
                                                <span className={`mx-1 font-bold ${operationResults.failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{operationResults.failed}</span> hata / yetkisiz.
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={() => setOperationResults(null)} className="text-gray-400 hover:text-gray-600 p-1">
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAKVİM & KAYITLI ATAMALAR (AYLIK VE YILLIK ÖNİZLEME) */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 flex flex-col overflow-hidden relative min-h-[560px]">
                        {!previewEmployeeId ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center glassmorphism-bg">
                                <div className="bg-blue-50 dark:bg-gray-800 p-5 rounded-2xl mb-4 ring-1 ring-blue-100 dark:ring-gray-700">
                                    <Calendar size={42} className="text-blue-500 dark:text-blue-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
                                    Personel Vardiya Takvimi (Aylık & Yıllık)
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md leading-relaxed">
                                    Sol listeden bir personeli seçtiğinizde veya isminin yanındaki <Eye size={16} className="inline mx-1 text-blue-500" /> simgesine tıkladığınızda personelin <strong>Aylık</strong> ve <strong>12 Aylık (Yıllık)</strong> vardiya takvimini burada inceleyebilirsiniz.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col h-full overflow-hidden">
                                {/* Üst Takvim Başlığı ve Aylık/Yıllık Kontrolleri */}
                                <div className="flex flex-wrap justify-between items-center px-5 pt-5 pb-4 gap-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/40 dark:bg-gray-800/60">
                                    <div className="flex items-center min-w-0">
                                        <div className="w-2 h-9 bg-blue-600 rounded-full mr-3 shrink-0"></div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h2 className="text-base font-extrabold text-gray-900 dark:text-white leading-tight truncate max-w-[220px] sm:max-w-[320px]">
                                                    {previewEmployee?.fullName}
                                                </h2>
                                                {previewEmployee?.workingRegimeName && (
                                                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-200/60 dark:border-indigo-800/60">
                                                        Rejim: {previewEmployee.workingRegimeName}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                {calendarMode === 'year'
                                                    ? `${viewDate.getFullYear()} Yılı 12 Aylık Vardiya Özeti`
                                                    : 'Aylık Vardiya Takvimi ve Atama Geçmişi'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Sağ: Aylık/Yıllık Mod Seçici + Tarih Navigasyonu */}
                                    <div className="flex flex-wrap items-center gap-2">
                                        {/* Aylık / Yıllık Segmented Switch */}
                                        <div className="flex items-center bg-gray-100 dark:bg-gray-900 p-1 rounded-xl border border-gray-200 dark:border-gray-700">
                                            <button
                                                type="button"
                                                onClick={() => setCalendarMode('month')}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                    calendarMode === 'month'
                                                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs'
                                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                                }`}
                                            >
                                                <Calendar size={14} />
                                                Aylık
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCalendarMode('year')}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                    calendarMode === 'year'
                                                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs'
                                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                                }`}
                                            >
                                                <CalendarDays size={14} />
                                                Yıllık (12 Ay)
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleTodayJump}
                                            className="px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                        >
                                            {calendarMode === 'year' ? 'Bu Yıl' : 'Bugün'}
                                        </button>

                                        {/* Ay / Yıl Seçici ve İleri-Geri Okları */}
                                        <div className="flex items-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-1 shadow-2xs">
                                            <button
                                                type="button"
                                                onClick={handlePrevPeriod}
                                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all"
                                                title={calendarMode === 'year' ? 'Önceki Yıl' : 'Önceki Ay'}
                                            >
                                                <ChevronLeft size={17} />
                                            </button>

                                            {calendarMode === 'month' ? (
                                                <div className="flex items-center gap-1 px-1">
                                                    <select
                                                        value={viewDate.getMonth()}
                                                        onChange={(e) => setViewDate(new Date(viewDate.getFullYear(), Number(e.target.value), 1))}
                                                        className="bg-transparent font-bold text-xs sm:text-sm text-gray-800 dark:text-gray-100 outline-none cursor-pointer pr-1"
                                                    >
                                                        {MONTH_NAMES_TR.map((mName, idx) => (
                                                            <option key={mName} value={idx} className="bg-white dark:bg-gray-800">
                                                                {mName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        value={viewDate.getFullYear()}
                                                        onChange={(e) => setViewDate(new Date(Number(e.target.value), viewDate.getMonth(), 1))}
                                                        className="bg-transparent font-bold text-xs sm:text-sm text-gray-800 dark:text-gray-100 outline-none cursor-pointer"
                                                    >
                                                        {yearOptions.map(y => (
                                                            <option key={y} value={y} className="bg-white dark:bg-gray-800">
                                                                {y}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <select
                                                    value={viewDate.getFullYear()}
                                                    onChange={(e) => setViewDate(new Date(Number(e.target.value), viewDate.getMonth(), 1))}
                                                    className="bg-transparent font-extrabold text-sm text-gray-800 dark:text-gray-100 outline-none cursor-pointer px-3"
                                                >
                                                    {yearOptions.map(y => (
                                                        <option key={y} value={y} className="bg-white dark:bg-gray-800">
                                                            {y}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}

                                            <button
                                                type="button"
                                                onClick={handleNextPeriod}
                                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all"
                                                title={calendarMode === 'year' ? 'Sonraki Yıl' : 'Sonraki Ay'}
                                            >
                                                <ChevronRight size={17} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Kayıtlı Atamalar Şeridi (Yatay Zaman Çizelgesi) */}
                                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60 bg-slate-50/60 dark:bg-gray-900/40">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                            Kayıtlı Vardiya Atamaları ({assignmentHistory.length})
                                        </span>
                                        {assignmentHistory.length > 0 && (
                                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                Atama kartına tıklayarak ilgili tarihe gidebilirsiniz
                                            </span>
                                        )}
                                    </div>

                                    {loadingCalendar ? (
                                        <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
                                            <Loader2 className="animate-spin text-blue-500" size={16} />
                                            <span>Atama geçmişi yükleniyor...</span>
                                        </div>
                                    ) : assignmentHistory.length === 0 ? (
                                        <div className="text-xs py-2.5 px-3.5 text-gray-500 dark:text-gray-400 border border-dashed rounded-xl border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/40">
                                            Bu personele ait henüz bir çalışma periyodu ataması bulunmuyor.
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2.5 overflow-x-auto pb-1 stylish-scrollbar">
                                            {assignmentHistory.map(assignment => {
                                                const isTemp = Boolean(assignment.endDate);
                                                return (
                                                    <div
                                                        key={assignment.id}
                                                        onClick={() => {
                                                            const d = new Date(assignment.startDate);
                                                            if (!isNaN(d.getTime())) {
                                                                setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
                                                            }
                                                        }}
                                                        className={`shrink-0 flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-all hover:shadow-sm ${
                                                            isTemp
                                                                ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60 hover:border-amber-400'
                                                                : 'bg-white dark:bg-gray-800 border-gray-200/90 dark:border-gray-700 hover:border-blue-400'
                                                        }`}
                                                    >
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="font-bold text-xs text-gray-800 dark:text-gray-100 truncate max-w-[160px]">
                                                                    {assignment.workPeriod?.name || 'Bilinmeyen'}
                                                                </span>
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                                                                    isTemp
                                                                        ? 'bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200'
                                                                        : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                                                }`}>
                                                                    {isTemp ? 'Geçici' : 'Sürekli'}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
                                                                <span className="text-blue-600 dark:text-blue-400">
                                                                    {new Date(assignment.startDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                </span>
                                                                <span className="mx-1 text-gray-400">➔</span>
                                                                <span>
                                                                    {assignment.endDate
                                                                        ? new Date(assignment.endDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
                                                                        : 'Sürekli'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {canDelete && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openDeleteAssignmentModal(assignment);
                                                                }}
                                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                                title="Atamayı Sil"
                                                            >
                                                                <Trash2 size={15} />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Görsel Takvim Alanı (Tam Genişlik Aylık veya 12 Aylık Yıllık) */}
                                <div className="flex-1 overflow-y-auto stylish-scrollbar">
                                    {loadingCalendar ? (
                                        <div className="flex items-center justify-center h-80">
                                            <Loader2 className="animate-spin text-blue-500/60" size={32} />
                                        </div>
                                    ) : (
                                        <CalendarView
                                            assignments={assignmentHistory}
                                            viewDate={viewDate}
                                            mode={calendarMode}
                                            onSelectMonth={handleSelectMonthFromYearView}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* VARDİYA ATAMASI SİLME & TARİH BOŞLUĞU KARAR MODALI */}
            {deleteModal.open && deleteModal.assignment && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full overflow-hidden">
                        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                    <Trash2 size={20} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                        Vardiya Atamasını Sil
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {previewEmployee?.fullName || deleteModal.assignment.employeeName}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={deleteModal.submitting}
                                onClick={() => setDeleteModal({ open: false, assignment: null, previousAssignment: null, submitting: false })}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-gray-900/70 border border-slate-200 dark:border-gray-700">
                                <div className="text-xs font-bold uppercase text-gray-400 mb-1">Silinecek Atama Kaydı</div>
                                <div className="font-bold text-sm text-gray-800 dark:text-gray-100">
                                    {deleteModal.assignment.workPeriod?.name || 'Bilinmeyen Periyot'}
                                </div>
                                <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mt-0.5">
                                    {new Date(deleteModal.assignment.startDate).toLocaleDateString('tr-TR')}
                                    {' ➔ '}
                                    {deleteModal.assignment.endDate
                                        ? new Date(deleteModal.assignment.endDate).toLocaleDateString('tr-TR')
                                        : 'Sürekli'}
                                </div>
                            </div>

                            {deleteModal.previousAssignment ? (
                                <div className="space-y-3">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                        Bu atama silindiğinde, ilgili tarih aralığı için <strong className="text-gray-900 dark:text-white">bir önceki vardiya ataması</strong> devreye alınsın mı, yoksa bu aralık <strong className="text-gray-900 dark:text-white">vardiyasız</strong> mı bırakılsın?
                                    </p>

                                    <button
                                        type="button"
                                        disabled={deleteModal.submitting}
                                        onClick={() => confirmDeleteAssignment(true)}
                                        className="w-full text-left p-3.5 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50/70 dark:bg-blue-900/20 hover:bg-blue-100/80 dark:hover:bg-blue-900/40 transition-all group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                                                <RotateCcw size={16} className="text-blue-600 dark:text-blue-400" />
                                                Araya Bir Önceki Vardiyayı Ata (Önerilen)
                                            </span>
                                            <ArrowRightLeft size={15} className="text-blue-500 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                        <p className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-1">
                                            Önceki vardiya olan <strong>"{deleteModal.previousAssignment.workPeriod?.name}"</strong> bu tarih aralığını kapsayacak şekilde uzatılır/birleştirilir; personel vardiyasız kalmaz.
                                        </p>
                                    </button>

                                    <button
                                        type="button"
                                        disabled={deleteModal.submitting}
                                        onClick={() => confirmDeleteAssignment(false)}
                                        className="w-full text-left p-3.5 rounded-xl border border-rose-200 dark:border-rose-800/60 bg-rose-50/50 dark:bg-rose-900/15 hover:bg-rose-100/60 dark:hover:bg-rose-900/30 transition-all"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-rose-700 dark:text-rose-300 flex items-center gap-2">
                                                <Trash2 size={16} className="text-rose-600 dark:text-rose-400" />
                                                Sadece Sil (Bu Aralıkta Vardiyasız Kalsın)
                                            </span>
                                        </div>
                                        <p className="text-xs text-rose-600/80 dark:text-rose-300/80 mt-1">
                                            Kayıt silinir ve bu tarih aralığına herhangi bir vardiya atanmaz (boş bırakılır).
                                        </p>
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Bu atamayı silmek istediğinizden emin misiniz? Bu kayıttan daha eski bir vardiya ataması bulunmadığı için ilgili tarih aralığı <strong>vardiyasız</strong> kalacaktır.
                                </p>
                            )}
                        </div>

                        <div className="px-5 py-3.5 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2.5">
                            <button
                                type="button"
                                disabled={deleteModal.submitting}
                                onClick={() => setDeleteModal({ open: false, assignment: null, previousAssignment: null, submitting: false })}
                                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100"
                            >
                                Vazgeç
                            </button>
                            {!deleteModal.previousAssignment && (
                                <button
                                    type="button"
                                    disabled={deleteModal.submitting}
                                    onClick={() => confirmDeleteAssignment(false)}
                                    className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 flex items-center gap-1.5 shadow-sm"
                                >
                                    {deleteModal.submitting && <Loader2 size={14} className="animate-spin" />}
                                    Evet, Atamayı Sil
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
